/**
 * Fetches one server-paged saas-auth tenant roster page (WSRPDM-0021 U2).
 * Search is supplied by the caller after its UI debounce. Status is sent to
 * the server (WSRPDM-0050, SASATH-0253) and omitted entirely for `ALL`.
 */
import { useCallback, useEffect, useState } from 'react';
import { listTenants } from '@/api/saas/tenants';
import type { PagedResponse } from '@/api/types';
import type { TenantDto } from '@/api/saas/types';

export type IdentityRosterLoadError = 'load-failed' | null;

export interface UseTenantIdentityRoster {
  result: PagedResponse<TenantDto> | null;
  loading: boolean;
  error: IdentityRosterLoadError;
  refetch: () => void;
}

export function useTenantIdentityRoster(
  page: number,
  size: number,
  query: string,
  status?: string,
): UseTenantIdentityRoster {
  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${page}:${size}:${query}:${status}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    result: PagedResponse<TenantDto> | null;
    error: IdentityRosterLoadError;
  }>({ requestKey: null, result: null, error: null });

  useEffect(() => {
    let cancelled = false;
    listTenants({ page, size, ...(query ? { q: query } : {}), ...(status ? { status } : {}) })
      .then((result) => {
        if (cancelled) return;
        setResponse({ requestKey, result, error: null });
      })
      .catch(() => {
        if (cancelled) return;
        setResponse({ requestKey, result: null, error: 'load-failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, reloadToken, requestKey, size, status]);

  const refetch = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const current = response.requestKey === requestKey;
  return {
    result: current ? response.result : null,
    loading: !current,
    error: current ? response.error : null,
    refetch,
  };
}
